/**
 * 盤面快照的新鮮度判定：快照回應可能比畫面上已套用的即時事件還舊，套下去會讓盤面退步。
 *
 * 判準以後端的毫秒時間戳為主（`board_time_ms` 對 `event_time_ms`），任一邊缺席時退回世代去重——
 * 往返期間收到過盤面事件就視為在途快照已作廢。
 *
 * 四條不變式：
 * - **任何會改動盤面畫面的事件都必須呼叫 `markEventApplied()`**，含獎項表與剩餘籤數那一半；
 *   漏掉的那則會讓世代停在原地，在途快照走「世代未變」的快路徑直接套用，把畫面蓋回舊值。
 *   新增盤面訂閱時，先問這則會不會改到快照也會寫的那些欄位。
 * - 丟棄快照是安全的。被丟掉的那份所缺的資訊，正是已經以 delta 形式套進畫面的那些事件；
 *   丟棄不會讓畫面退步，只會少一次全量對齊。
 * - 補排重抓有上限。活躍盤面的事件持續抵達，無上限會陷入「抓→丟→再抓」的迴圈；
 *   上限用完後畫面仍由 delta 維持正確，只是少了全量對齊這道保險。
 * - `beginFetch()` 與 `evaluate()` 必須成對。同時只會有一個快照在途（`createRefetchScheduler`
 *   的 in-flight 合流保證），因此不需要管理多個併存的 token。
 *
 * @param {{maxConsecutiveDiscards?: number}} [options]
 * @returns {{
 *   beginFetch: function(): {generation: number},
 *   markEventApplied: function(*): void,
 *   evaluate: function({generation: number}, *): {apply: boolean, refetch: boolean},
 *   reset: function(): void,
 * }}
 */
export function createBoardFreshness({ maxConsecutiveDiscards = 3 } = {}) {
    let generation = 0;
    let latestEventTimeMs;
    let allEventsTimed = true;
    let consecutiveDiscards = 0;

    function beginFetch() {
        return { generation };
    }

    function markEventApplied(eventTimeMs) {
        generation += 1;
        if (typeof eventTimeMs === 'number' && Number.isFinite(eventTimeMs)) {
            latestEventTimeMs = latestEventTimeMs === undefined
                ? eventTimeMs
                : Math.max(latestEventTimeMs, eventTimeMs);
            return;
        }
        allEventsTimed = false;
    }

    function reset() {
        consecutiveDiscards = 0;
    }

    function discard() {
        consecutiveDiscards += 1;
        return { apply: false, refetch: consecutiveDiscards <= maxConsecutiveDiscards };
    }

    function accept() {
        consecutiveDiscards = 0;
        allEventsTimed = true;
        return { apply: true, refetch: false };
    }

    function evaluate(token, boardTimeMs) {
        if (token.generation === generation) {
            return accept();
        }
        const comparable = allEventsTimed
            && typeof boardTimeMs === 'number' && Number.isFinite(boardTimeMs)
            && typeof latestEventTimeMs === 'number';
        if (comparable && boardTimeMs >= latestEventTimeMs) {
            return accept();
        }
        return discard();
    }

    return { beginFetch, markEventApplied, evaluate, reset };
}
